import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/useAuth';
import { apiClient } from '@/services/api';
import { LeaderboardTable } from '@/components/LeaderboardTable';
import { MainSection } from '@/components/layout/MainSection';
import { SectionContent } from '@/components/layout/SectionContent';
import { useTournamentDetailContext } from '@/layouts/TournamentDetailLayout/context';
import type { LeaderboardData } from '@/layouts/TournamentDetailLayout/context';

/**
 * Tournament "Рейтинг" tab — leaderboard table for `/tournaments/:id/leaderboard`.
 *
 * Per-participant statistics now lives in its own standalone layout
 * (`/tournaments/:id/leaderboard/:userId/*`, see ParticipantStatsLayout).
 * Row clicks here link to that layout.
 */
const LeaderboardTab: React.FC = () => {
  const { tournament, tournamentId } = useTournamentDetailContext();
  const { isAuthenticated } = useAuth();
  const { t } = useTranslation(['tournament']);

  const { data: leaderboardData } = useQuery<LeaderboardData>({
    queryKey: ['tournament-leaderboard', tournamentId],
    queryFn: async () => {
      const response = await apiClient.getTournamentLeaderboard(tournamentId, 100);
      return response.data?.data || response.data || response;
    },
    enabled: !!tournamentId && isAuthenticated,
  });

  const leaderboard = leaderboardData?.leaderboard || [];

  return (
    <MainSection
      hideTitleOnDesktop
      title={tournament?.name || 'Tournament'}
      subtitle={t('tournament:leaderboard')}
      logoUrl={tournament?.league?.logo_url || tournament?.leagueLogo}
      logoAlt={tournament?.league?.name || tournament?.leagueName}
    >
      <SectionContent>
        <LeaderboardTable
          noWrapper
          title={tournament?.name || 'Tournament'}
          subtitle={t('tournament:leaderboard')}
          entries={leaderboard}
          emptyMessage={t('tournament:leaderboardWillAppear')}
          linkBuilder={entry => {
            const uid = entry.user_id ?? entry.id;
            return uid ? `/tournaments/${tournamentId}/leaderboard/${uid}` : null;
          }}
        />
      </SectionContent>
    </MainSection>
  );
};

export default LeaderboardTab;
